import { createFileRoute /*, Link, useNavigate*/ } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import { requestNgoLoginOptions } from '@/lib/queries-ngo';
import { Button } from '@worldcoo/ui/components/button';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { toast } from '@/lib/toast';
import { DonatingIllustration } from '@worldcoo/ui/custom/donating-illustration';
import { useAppForm } from '@/lib/use-app-form';
import { NgoLanguageSelect } from './-components/ngo-language-select';
import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import { i18n } from '@lingui/core';
import * as z from 'zod';
import * as zm from 'zod/mini';

export const Route = createFileRoute('/ngos/login')({
	validateSearch: zm.object({
		redirect: zm.catch(zm.optional(zm.string()), undefined),
	}),
	component: LoginPage,
});

function LoginPage() {
	const { t } = useLingui();
	// const navigate = useNavigate();
	const { redirect } = Route.useSearch();

	const { mutateAsync: requestLogin, isPending } = useMutation({
		...requestNgoLoginOptions(),
		onSuccess: (result) => {
			if ('captchaPassed' in result && !result.captchaPassed) {
				toast.error(t`Captcha verification failed. Please try again.`);
				return;
			}
			toast.success(t`We've sent a login link to your email`);
		},
		onError: () => {
			toast.error(t`Something went wrong. Please try again.`);
		},
	});

	const form = useAppForm({
		defaultValues: {
			email: '',
		},
		validators: {
			onSubmit: z.object({
				email: z.string().min(1),
			}),
		},
		onSubmit: async ({ value }) => {
			if (!value.email.trim()) return;
			const captchaToken = await generateCaptchaToken();
			await requestLogin({ email: value.email.trim(), locale: i18n.locale || undefined, captchaToken, redirect });
		},
	});

	return (
		<div
			data-page="login"
			className="relative flex min-h-screen bg-white"
			style={{ viewTransitionName: 'ngo-main-content' }}
		>
			<div className="absolute bottom-6 left-6 px-12 py-6">
				<NgoLanguageSelect />
			</div>
			{/* Left side */}
			<div className="flex flex-1 flex-col p-20">
				<WorldcooLogo accentClassName="text-[#f28e41]" className="h-[80px] w-auto self-start" />

				<div className="flex flex-1 items-center">
					<div className="flex w-full max-w-[459px] flex-col gap-8">
						<div className="flex flex-col gap-2 text-[#35433f]">
							<h1 className="text-[32px] leading-[38px] font-medium tracking-[-0.2px]">
								{t`Log in into your account`}
							</h1>
							<p className="text-base leading-normal">{t`Enter your email and we'll send you a login link`}</p>
						</div>

						<form
							onSubmit={(e) => {
								e.preventDefault();
								form.handleSubmit();
							}}
							className="flex flex-col gap-6"
						>
							<form.AppField
								name="email"
								children={(field) => (
									<field.TextField label={t`Username`} placeholder={t`Enter your email`} className="[&_input]:h-10" />
								)}
							/>

							<Button type="submit" className="h-10 w-fit" disabled={isPending}>
								{isPending ? t`Sending...` : t`Send login link to email`}
							</Button>
						</form>

						{/* <p className="mt-6 text-center text-sm text-[#6b7280]">
							{t`Don't have an account?`}{' '}
							<Link
								to="/ngos/onboarding"
								className="font-medium text-[#f28e41] hover:underline"
								onClick={(e) => {
									e.preventDefault();
									if (!document.startViewTransition) {
										navigate({ to: '/ngos/onboarding' });
										return;
									}
									document.startViewTransition(() => {
										navigate({ to: '/ngos/onboarding' });
										return new Promise<void>((resolve) => {
											const observer = new MutationObserver(() => {
												if (document.querySelector('[data-page="onboarding"]')) {
													observer.disconnect();
													resolve();
												}
											});
											observer.observe(document.body, { childList: true, subtree: true });
											setTimeout(() => {
												observer.disconnect();
												resolve();
											}, 3000);
										});
									});
								}}
							>
								{t`Register your organization`}
							</Link>
						</p> */}
					</div>
				</div>
			</div>

			{/* Right side - Illustration panel */}
			<div className="hidden w-1/2 p-6 pl-0 lg:block">
				<div className="flex h-full items-center justify-center rounded-lg bg-[#fff7ed]">
					<DonatingIllustration className="text-primary -mr-18 size-[599px]" />
				</div>
			</div>
		</div>
	);
}
